import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import PricingModelEditor, { PricingModelInfo, ValidationResult } from "src/components/PricingModelEditor";
import { CellError } from "src/components/TieredPricingEditor";
import styles from "./ProductPricingModels.module.css";

interface PricingModelDetail extends PricingModelInfo {
  providerId: number;
  productId: number;
  serializedType: string;
  json: string;
}

const API_BASE = "sysadmin/product-pricing-models";

const EditProductPricingModel: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const navigate = useNavigate();

  const [model, setModel] = useState<PricingModelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadModel();
  }, [modelId]);

  const loadModel = async () => {
    try {
      setLoading(true);
      setError(null);
      const modelData = await callApi(`${API_BASE}/${modelId}`, HttpMethod.GET);
      setModel(modelData);
    } catch {
      setError("Failed to load pricing model.");
    } finally {
      setLoading(false);
    }
  };

  // Replace empty/whitespace strings with null before sending to server
  const sanitizeJson = (json: string): string => {
    const parsed = JSON.parse(json);
    const clean = (obj: unknown): unknown => {
      if (obj === null || obj === undefined) return obj;
      if (typeof obj === "string") return obj.trim() === "" ? null : obj;
      if (Array.isArray(obj)) return obj.map(clean);
      if (typeof obj === "object") return Object.fromEntries(Object.entries(obj as Record<string, unknown>).map(([k, v]) => [k, clean(v)]));
      return obj;
    };
    return JSON.stringify(clean(parsed));
  };

  const handleSave = async (json: string, skuId: number | null): Promise<CellError[] | void> => {
    const sanitized = sanitizeJson(json);
    const result = await callApi(`${API_BASE}/${modelId}`, HttpMethod.PUT, {
      acctId: model!.acctId?.trim() || null,
      providerId: model!.providerId,
      json: sanitized,
      skuId,
    });
    if (result.validationErrors) {
      return result.validationErrors as CellError[];
    }
    if (result.type === "error") {
      toast.error(result.message);
    } else {
      toast.success(result.message || "Model updated.");
      navigate("/sysadmin/product-pricing-models");
    }
  };

  const handleValidate = async (json: string, skuId: number | null): Promise<ValidationResult> => {
    const sanitized = sanitizeJson(json);
    const result = await callApi(`${API_BASE}/${modelId}/validate`, HttpMethod.POST, { json: sanitized, skuId });
    const errors: CellError[] = (result.validationErrors ?? []).map((e: { row: string; field: string; message: string }) => ({
      row: e.row === "default" ? "default" : Number(e.row),
      field: e.field,
      message: e.message,
    }));
    const purchaseInfo = errors.length === 0 ? (result.purchaseInfo ?? []) : null;
    return { errors, purchaseInfo };
  };

  if (loading) return <div className={styles.container}>Loading…</div>;
  if (error) return <div className={styles.container}><div className="alert alert-danger">{error}</div></div>;
  if (!model) return <div className={styles.container}><div className="alert alert-warning">Model not found.</div></div>;

  return (
    <div className={styles.container}>
      <PricingModelEditor
        model={model}
        initialJson={model.json ?? "{}"}
        onSave={handleSave}
        onValidate={handleValidate}
        onCancel={() => navigate("/sysadmin/product-pricing-models")}
      />
    </div>
  );
};

export default EditProductPricingModel;
